import React, { useState } from 'react';
import { StepOne } from './StepOne/StepOne';
import { StepTwo } from './StepTwo/StepTwo';
import { getHeadingsMapping } from './utils/getHeadingsMapping';
import { Sidebar } from '../Sidebar/Sidebar';
import { isLoggedIn } from './utils/isLoggedIn';
import { getStepName } from './utils/getStepName';
import { getObjectFromFormData } from '../../assets/js/utils/getObjectFromEntries';
import { hasErrors } from './utils/hasErrors';
import { fireOrganizationChangedEvent } from './utils/fireOrganizationChangedEvent';
import { fireLoggedInEvent } from './utils/fireLoggedInEvent';
import { emptyInputAndFocus } from './utils/emptyInputAndFocus';
import { getOrganizationsByAvailability } from './utils/getOrganizationsByAvailability';
import { fetchLogin } from './utils/fetchLogin';
import { fetchSelectOrganization } from './utils/fetchSelectOrganization';
import { addToCartCount } from '../../assets/js/utils/addToCartCount';
import { updateAntiForgeryTokenFieldValues } from './utils/updateAntiForgeryTokenFieldValues';
import { handleGtmEvent } from '../../assets/js/utils/gtm';

/** Note that this component is both mounted on an HTML-page (sign-in page) as well as used as a child-component (in PriceCalculation). **/
function Login({
    meData: meDataProp,
    organizationData: organizationDataProp,
    productData,
    redirectUrl,
    sidebarData,
    forgotPasswordData,
    selectOrganizationApiUrl,
    signInApiUrl,
    selectedOrganizationId: selectedOrgIdProp,
    loginType,
    loggedInCallback,
    resourcedTexts,
}) {
    const [clientSideStepOneErrors, setClientSideStepOneErrors] = useState({
        email: resourcedTexts.requiredEmailValidation,
        password: resourcedTexts.requiredPasswordValidation,
    });

    const [clientSideStepOneTouched, setClientSideStepOneTouched] = useState({
        email: false,
        password: false,
    });

    const [serverSideStepOneErrors, setServerSideStepOneErrors] = useState([]);
    const [meData, setMeData] = useState(meDataProp);
    const [organizationData, setOrganizationData] = useState(organizationDataProp);
    const [selectedOrgId, setSelectedOrgId] = useState(selectedOrgIdProp);
    const [isLoading, setIsLoading] = useState(false);

    const { availableOrganizations, unavailableOrganizations } = getOrganizationsByAvailability(
        organizationData,
        productData,
    );

    const noOrganizationAvailable = availableOrganizations.length === 0;
    const stepName = getStepName(loginType, isLoggedIn(meData), noOrganizationAvailable);
    const { title, mainHeading } = getHeadingsMapping(stepName, resourcedTexts, productData);
    const isFirstStep = !isLoggedIn(meData);
    const isSecondStep = !isFirstStep;

    async function handleLogin(formEl, email, password) {
        setIsLoading(true);

        try {
            const responseJson = await fetchLogin(signInApiUrl, email, password);
            const { errors, meData, organizationData, selectedOrganizationId, cartCount } = responseJson;

            if (errors && errors.length > 0) {
                setServerSideStepOneErrors(errors);
                emptyInputAndFocus(formEl, 'password');
                setIsLoading(false);

                handleGtmEvent('login_failed');

                return;
            }

            setSelectedOrgId(selectedOrganizationId);
            setMeData({ ...meData });
            setOrganizationData({ ...organizationData });
            setServerSideStepOneErrors([]);
            setIsLoading(false);
            fireLoggedInEvent(meData, organizationData, selectedOrganizationId);
            updateAntiForgeryTokenFieldValues();

            handleGtmEvent('login_success');

            if (cartCount && cartCount > 0) {
                addToCartCount(cartCount);
            }

            if (loginType === 'priceCalculation') {
                loggedInCallback(organizationData);
            }
        } catch (error) {
            setServerSideStepOneErrors([resourcedTexts.serverErrorSomethingWentWrong]);
            setIsLoading(false);
            console.error('error', error.message);
        }
    }

    async function handleSelectOrganization(id) {
        setIsLoading(true);

        try {
            const selectedOrganization = organizationData.organizations.find((org) => org.id === id);

            await fetchSelectOrganization(selectOrganizationApiUrl, selectedOrganization);
            fireOrganizationChangedEvent(id);

            handleGtmEvent('login_organization_select');

            window.location.href = redirectUrl;
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('error', error.message);
        }
    }

    function handleSubmit(e) {
        e.preventDefault();
        const formEl = e.currentTarget;
        const formData = new FormData(formEl);
        const formDataObject = getObjectFromFormData(formData);

        if (isFirstStep) {
            setClientSideStepOneTouched({ email: true, password: true });
            if (hasErrors(clientSideStepOneErrors)) return;
            const { email, password } = formDataObject;
            handleLogin(formEl, email, password);
        }

        if (isSecondStep) {
            const organizationId = formDataObject['organization'];
            if (!organizationId) return;
            handleSelectOrganization(organizationId);
        }
    }

    function handleInputValidity(inputEl) {
        const { name, value } = inputEl;
        const isRequired = inputEl.hasAttribute('required');
        const isEmpty = value.length === 0;
        const isError = !inputEl.checkValidity();

        const errorsCopy = { ...clientSideStepOneErrors };
        switch (name) {
            case 'email':
                if (isRequired && isEmpty) {
                    errorsCopy[name] = resourcedTexts.requiredEmailValidation;
                } else if (isError) {
                    errorsCopy[name] = resourcedTexts.invalidEmailValidation;
                } else {
                    errorsCopy[name] = null;
                }
                break;
            case 'password':
                if (isRequired && isEmpty) {
                    errorsCopy[name] = resourcedTexts.requiredPasswordValidation;
                } else {
                    errorsCopy[name] = null;
                }
        }

        setClientSideStepOneErrors(errorsCopy);
    }

    function handleInputChange({ target }) {
        handleInputValidity(target);
    }

    function handleInputBlur({ currentTarget }) {
        if (currentTarget.value.length === 0) return;

        setClientSideStepOneTouched({
            ...clientSideStepOneTouched,
            [currentTarget.name]: true,
        });
    }

    return (
        <div className="login">
            <h2 className="login__title">{title}</h2>

            <div className="row">
                <div className="col-12 col-md-7">
                    <form className="login__main" onSubmit={handleSubmit} noValidate>
                        {mainHeading && <h3 className="login__main-heading">{mainHeading}</h3>}

                        {isFirstStep ? (
                            <StepOne
                                handleInputChange={handleInputChange}
                                handleInputBlur={handleInputBlur}
                                forgotPasswordData={forgotPasswordData}
                                clientSideErrors={clientSideStepOneErrors}
                                clientSideTouched={clientSideStepOneTouched}
                                serverSideErrors={serverSideStepOneErrors}
                                isLoading={isLoading}
                                autoFocus={loginType === 'standAlone'}
                                resourcedTexts={resourcedTexts}
                            />
                        ) : (
                            <StepTwo
                                availableOrganizations={availableOrganizations}
                                unavailableOrganizations={unavailableOrganizations}
                                selectedOrganizationId={selectedOrgId}
                                setSelectedOrganizationId={setSelectedOrgId}
                                isLoading={isLoading}
                                resourcedTexts={resourcedTexts}
                            />
                        )}
                    </form>
                </div>
                <div className="col-12 col-md-5">
                    <Sidebar sidebarData={sidebarData} stepName={stepName} />
                </div>
            </div>
        </div>
    );
}

export { Login };
